/**
 * Interpretation generators for Deal Workspace.
 * Rule-based, Phase 1 — no AI dependency.
 * Logic follows docs/pm/specs/kpi-interpretationen.md exactly.
 */

/* ── Deal Interpretation ── */
export function generateDealInterpretation(scoreDetails, market) {
  const parts = [];
  const dealDelta = scoreDetails?.deal?.dealDelta ?? market?.priceDeviation ?? null;
  const marketPricePerSqm = scoreDetails?.deal?.marketPricePerSqm ?? market?.avg ?? null;
  const city = market?.matchedCity || 'dieser Stadt';
  const faktor = scoreDetails?.deal?.faktor ?? null;

  if (dealDelta != null) {
    const absDelta = Math.abs(Math.round(dealDelta * 100));
    if (dealDelta < -0.20) {
      parts.push(`Kaufpreis liegt ${absDelta}% unter dem Marktdurchschnitt von ${marketPricePerSqm ? Math.round(marketPricePerSqm).toLocaleString('de-DE') : '?'} €/m² — sehr günstiger Einstieg.`);
    } else if (dealDelta < -0.05) {
      parts.push(`Kaufpreis liegt ${absDelta}% unter dem Marktdurchschnitt — guter Verhandlungserfolg oder unterbewertetes Objekt.`);
    } else if (dealDelta <= 0.05) {
      parts.push(`Kaufpreis liegt im Marktdurchschnitt für ${city}.`);
    } else {
      parts.push(`Kaufpreis liegt ${absDelta}% über dem Marktdurchschnitt — prüfe ob Ausstattung/Zustand den Aufpreis rechtfertigen.`);
    }
  }

  if (faktor != null) {
    const f = typeof faktor === 'number' ? faktor : parseFloat(faktor);
    if (f < 15) {
      parts.push(`Kaufpreisfaktor ${f.toFixed(1)}× ist ausgezeichnet (unter 15×).`);
    } else if (f <= 20) {
      parts.push(`Kaufpreisfaktor ${f.toFixed(1)}× ist gut (unter 20×).`);
    } else if (f <= 25) {
      parts.push(`Kaufpreisfaktor ${f.toFixed(1)}× ist akzeptabel.`);
    } else {
      parts.push(`Kaufpreisfaktor ${f.toFixed(1)}× ist hoch — typisch für A-Städte.`);
    }
  }

  return parts.join(' ') || null;
}

/* ── Cashflow Interpretation (legacy, still used in KPI drill-down) ── */
export function generateCashflowInterpretation(cashflowData, baseline) {
  const cf = cashflowData?.cashflow ?? baseline?.cf?.cashflow ?? null;
  const tilgung = cashflowData?.davonTilgung ?? baseline?.cf?.davonTilgung ?? 0;

  if (cf == null) return null;

  if (cf > 200) {
    return 'Starker positiver Cashflow. Die Miete deckt alle Kosten und erzeugt Überschuss. Solide Basis für Vermögensaufbau.';
  }
  if (cf >= 0) {
    return 'Leicht positiver Cashflow — die Immobilie trägt sich selbst.';
  }
  if (cf >= -150) {
    const eigenaufwand = Math.abs(cf) - tilgung;
    return `Leicht negativer Cashflow. Du zahlst ${Math.abs(cf)}€/Mo zu. Davon fließen ${tilgung}€ in Tilgung (= Vermögensaufbau). Effektiver Eigenaufwand: ${Math.max(eigenaufwand, 0)}€/Mo.`;
  }
  return `Deutlich negativer Cashflow. Monatliche Zuzahlung von ${Math.abs(cf)}€ nötig. Nur sinnvoll wenn Wertsteigerung oder Mieterhöhungspotenzial den Aufwand kompensieren.`;
}

/* ── Income Interpretation (Scoring V3, financing-independent) ── */
export function generateIncomeInterpretation(scoreDetails, baseline) {
  const bruttoRendite = scoreDetails?.income?.bruttoRendite ?? baseline?.bruttoRendite ?? null;
  const cf = scoreDetails?.income?.cashflow ?? baseline?.cf?.cashflow ?? null;

  if (bruttoRendite != null) {
    const renditeStr = bruttoRendite.toFixed(1).replace('.', ',');
    if (bruttoRendite >= 6) {
      return `Bruttorendite von ${renditeStr}% — sehr stark. Die Miete erzeugt einen deutlichen Überschuss über alle Kosten.`;
    }
    if (bruttoRendite >= 5) {
      return `Bruttorendite von ${renditeStr}% — gut. Solide Basis für positiven Cashflow bei üblicher Finanzierung.`;
    }
    if (bruttoRendite >= 4) {
      return `Bruttorendite von ${renditeStr}% — akzeptabel. Cashflow-neutral bis leicht negativ, je nach Finanzierungsstruktur.`;
    }
    if (bruttoRendite >= 3) {
      return `Bruttorendite von ${renditeStr}% — niedrig. Cashflow dürfte negativ sein — Investmenttauglich nur über Wertsteigerung.`;
    }
    return `Bruttorendite von ${renditeStr}% — sehr niedrig. Ertragsbasiert kaum zu rechtfertigen.`;
  }

  // Fallback if bruttoRendite not available
  if (cf != null) {
    if (cf > 200) return 'Starker positiver Cashflow — Ertrag deckt alle Kosten mit Puffer.';
    if (cf >= 0) return 'Die Immobilie trägt sich selbst.';
    if (cf >= -150) return `Leicht negativer Cashflow von ${Math.abs(cf)}€/Mo — im akzeptablen Bereich.`;
    return `Deutlich negativer Cashflow von ${Math.abs(cf)}€/Mo — hohe monatliche Zuzahlung nötig.`;
  }

  return null;
}

/* ── Object Interpretation (Scoring V3) ── */
export function generateObjectInterpretation(scoreDetails, signals) {
  const parts = [];
  const baujahr = scoreDetails?.object?.baujahr ?? null;
  const breakEvenZins = scoreDetails?.object?.breakEvenZins ?? null;
  const currentZins = scoreDetails?.financing?.zinssatz ?? 0.035;

  const risks = signals?.filter(s => s.type === 'risk') || [];
  const cautions = signals?.filter(s => s.type === 'caution') || [];
  const positives = signals?.filter(s => s.type === 'positive') || [];

  if (risks.length === 0 && cautions.length === 0) {
    parts.push('Keine erkennbaren Objektrisiken — solides Profil.');
  } else {
    const total = risks.length + cautions.length;
    parts.push(`${total} Warnsignal${total > 1 ? 'e' : ''} erkannt.`);
    if (risks[0]?.label) parts.push(`Wichtigstes Risiko: ${risks[0].label}.`);
  }

  if (baujahr != null) {
    const alter = new Date().getFullYear() - baujahr;
    if (alter > 50) {
      parts.push(`Baujahr ${baujahr} — Altbau. Renovierungsbedarf prüfen.`);
    } else if (alter > 25) {
      parts.push(`Baujahr ${baujahr} — mittleres Alter.`);
    } else {
      parts.push(`Baujahr ${baujahr} — modernes Objekt.`);
    }
  }

  if (positives.length > 0) {
    parts.push(`${positives.length} positive${positives.length > 1 ? ' Signale' : 's Signal'} vorhanden.`);
  }

  if (breakEvenZins != null) {
    const beZins = (breakEvenZins * 100).toFixed(1).replace('.', ',');
    const puffer = ((breakEvenZins - currentZins) * 100).toFixed(1).replace('.', ',');
    parts.push(`Zinspuffer bis Break-Even: ${puffer} Prozentpunkte (Break-Even bei ${beZins}%).`);
  }

  return parts.join(' ') || null;
}

/* ── Location Interpretation ── */
export function generateLocationInterpretation(scoreDetails, market) {
  const parts = [];
  const city = market?.matchedCity || 'dieser Stadt';
  const cityCategory = scoreDetails?.location?.cityCategory ?? null;
  const grossYield = scoreDetails?.location?.grossYield ?? null;

  if (cityCategory === 'A') {
    parts.push(`${city} ist eine A-Stadt — höchste Nachfrage, stabiler Markt, langfristiger Werterhalt.`);
  } else if (cityCategory === 'B') {
    parts.push(`${city} ist eine B-Stadt — solide Nachfrage, gutes Rendite-Risiko-Verhältnis.`);
  } else {
    parts.push(`Keine A/B-Stadt-Einstufung. Lokale Marktkenntnis wichtig — prüfe Bevölkerungsentwicklung und Wirtschaftslage.`);
  }

  if (grossYield != null) {
    const yieldPct = (grossYield * 100).toFixed(1).replace('.', ',');
    const avgYield = cityCategory === 'A' ? 3.5 : cityCategory === 'B' ? 4.5 : 5.0;
    const yieldVal = grossYield * 100;
    if (yieldVal > avgYield + 0.5) {
      parts.push(`Bruttorendite von ${yieldPct}% liegt über dem Durchschnitt für ${city}.`);
    } else if (yieldVal < avgYield - 0.5) {
      parts.push(`Bruttorendite von ${yieldPct}% liegt unter dem Durchschnitt für ${city}.`);
    } else {
      parts.push(`Bruttorendite von ${yieldPct}% liegt im Durchschnitt für ${city}.`);
    }
  }

  return parts.join(' ') || null;
}

/* ── Risk Interpretation ── */
export function generateRiskInterpretation(scoreDetails, signals) {
  const parts = [];
  const riskScore = scoreDetails?.risk?.total ?? null;
  const risks = signals?.filter(s => s.type === 'risk') || [];
  const chances = signals?.filter(s => s.type === 'positive') || [];
  const topRisk = risks[0]?.label || null;
  const breakEvenZins = scoreDetails?.risk?.breakEvenZins ?? null;
  const currentZins = scoreDetails?.financing?.zinssatz ?? 0.035;

  if (riskScore != null) {
    if (riskScore >= 16) {
      parts.push('Geringes Risikoprofil.');
    } else if (riskScore >= 10) {
      parts.push(`Moderates Risiko.${topRisk ? ` ${topRisk} beachten.` : ''}`);
    } else {
      parts.push(`Erhöhtes Risiko. ${risks.length} Risikosignale erkannt.`);
    }
  }

  if (chances.length > 0) {
    parts.push(`Positiv: ${chances.length} Chancen-Signale.`);
  }

  if (breakEvenZins != null) {
    const beZins = (breakEvenZins * 100).toFixed(1).replace('.', ',');
    const curZins = (currentZins * 100).toFixed(1).replace('.', ',');
    const puffer = ((breakEvenZins - currentZins) * 100).toFixed(1).replace('.', ',');
    parts.push(`Break-Even-Zins: ${beZins}%. Bei aktuellem Zins von ${curZins}% besteht ein Puffer von ${puffer} Prozentpunkten.`);
  }

  return parts.join(' ') || null;
}

/* ── KPI Context for Drill-down Popups ── */

const KPI_DEFINITIONS = {
  bruttomietrendite: {
    title: 'Bruttomietrendite',
    definition: 'Verhältnis der Jahreskaltmiete zum Kaufpreis. Erste schnelle Einschätzung der Rentabilität vor Finanzierungskosten.',
    formula: 'Jahreskaltmiete / Kaufpreis',
  },
  nettomietrendite: {
    title: 'Nettomietrendite',
    definition: 'Rendite nach Abzug der Bewirtschaftungskosten, bezogen auf die Gesamtinvestition. Realistischer als die Bruttomietrendite.',
    formula: '(Jahreskaltmiete - Bewirtschaftung) / Gesamtkosten',
  },
  kaufpreisfaktor: {
    title: 'Kaufpreisfaktor',
    definition: 'Wie viele Jahreskaltmieten kostet die Immobilie? Je niedriger, desto schneller verdienst du den Kaufpreis zurück.',
    formula: 'Kaufpreis / Jahreskaltmiete',
  },
  cashflow: {
    title: 'Cashflow',
    definition: 'Monatlicher Überschuss nach Zins und Bewirtschaftung. Tilgung ist Vermögensaufbau — kein echter Kostenfaktor — und wird daher nicht abgezogen.',
    formula: 'Kaltmiete - Zins - Bewirtschaftung (1 €/m²)',
  },
  ekRendite: {
    title: 'EK-Rendite',
    definition: 'Rendite auf dein eingesetztes Eigenkapital. Zeigt den Hebeleffekt der Fremdfinanzierung.',
    formula: 'Cashflow (Jahr) / Eigenkapital',
  },
};

export function generateKpiContext(kpiName, value, recalcData) {
  const def = KPI_DEFINITIONS[kpiName];
  if (!def) return null;

  const baseline = recalcData?._baseline || {};
  const market = recalcData?.marketData || {};
  const cf = baseline.cf || {};
  const city = market.matchedCity || 'dieser Stadt';

  const result = {
    title: def.title,
    definition: def.definition,
    formulaLabel: def.formula,
    calculation: null,
    interpretation: null,
    rating: null,
  };

  switch (kpiName) {
    case 'bruttomietrendite': {
      const miete = baseline.monatskaltmiete || 0;
      const jahresmiete = miete * 12;
      const gesamtkosten = baseline.gesamtkosten || baseline.kaufpreis || 0;
      const kp = baseline.kaufpreis || 0;
      const rendite = gesamtkosten > 0 ? (jahresmiete / gesamtkosten * 100) : 0;
      const rentPerSqm = market.avgRentPerSqm;
      const area = baseline.wohnflaeche;
      const mietHerkunft = baseline._mietHerkunft || (rentPerSqm ? `\u00D8 Angebotsmiete ${city}` : 'Pauschalansatz');

      result.calculation = [
        { label: 'Kaltmiete', value: `${Math.round(miete).toLocaleString('de-DE')} €/Mo`, source: mietHerkunft },
        { label: 'Jahresmiete', value: `${Math.round(jahresmiete).toLocaleString('de-DE')} €`, source: `${Math.round(miete).toLocaleString('de-DE')} € × 12` },
        { label: 'Gesamtkosten', value: `${Math.round(gesamtkosten).toLocaleString('de-DE')} €`, source: 'Kaufpreis + Nebenkosten',
          details: baseline.nk ? [
            { label: 'Kaufpreis', value: `${Math.round(kp).toLocaleString('de-DE')} €` },
            { label: 'Grunderwerbsteuer', value: `${Math.round(baseline.nk.gpiEst || 0).toLocaleString('de-DE')} €`, pct: `${((baseline.nk.gpiEstSatz || 0) * 100).toFixed(1)}%` },
            { label: 'Makler', value: `${Math.round(baseline.nk.makler || 0).toLocaleString('de-DE')} €`, pct: `${((baseline.maklerAnteil || 0.0357) * 100).toFixed(2)}%` },
            { label: 'Notar', value: `${Math.round(baseline.nk.notar || 0).toLocaleString('de-DE')} €`, pct: `${(kp > 0 ? ((baseline.nk.notar || 0) / kp) * 100 : 0).toFixed(1)}%` },
          ] : null },
        { label: 'Rendite', value: `${rendite.toFixed(2).replace('.', ',')}%`, source: `${Math.round(jahresmiete).toLocaleString('de-DE')} / ${Math.round(gesamtkosten).toLocaleString('de-DE')}` },
      ];

      if (rendite >= 5) { result.rating = 'gut'; result.interpretation = `Mit ${rendite.toFixed(1).replace('.', ',')}% liegt die Rendite über dem Durchschnitt. Solider Wert für die Lage.`; }
      else if (rendite >= 3.5) { result.rating = 'akzeptabel'; result.interpretation = `${rendite.toFixed(1).replace('.', ',')}% ist durchschnittlich — typisch für gute Lagen.`; }
      else { result.rating = 'kritisch'; result.interpretation = `${rendite.toFixed(1).replace('.', ',')}% ist unterdurchschnittlich. Nur über Wertsteigerung zu rechtfertigen.`; }
      break;
    }

    case 'nettomietrendite': {
      const miete = baseline.monatskaltmiete || 0;
      const jahresmiete = miete * 12;
      const bewirtschaftung = cf.bewirtschaftung ?? Math.round((baseline.wohnflaeche || 0) * 1.00);
      const jahresBewirtschaftung = bewirtschaftung * 12;
      const gesamtkosten = baseline.gesamtkosten || baseline.kaufpreis || 0;
      const nettoRendite = gesamtkosten > 0 ? ((jahresmiete - jahresBewirtschaftung) / gesamtkosten * 100) : 0;

      result.calculation = [
        { label: 'Jahreskaltmiete', value: `${Math.round(jahresmiete).toLocaleString('de-DE')} €`, source: `${Math.round(miete).toLocaleString('de-DE')} € × 12` },
        { label: 'Bewirtschaftung (Jahr)', value: `-${Math.round(jahresBewirtschaftung).toLocaleString('de-DE')} €`, source: `1 €/m² × ${baseline.wohnflaeche || 0} m² × 12` },
        { label: 'Gesamtkosten', value: `${Math.round(gesamtkosten).toLocaleString('de-DE')} €`, source: 'Kaufpreis + Nebenkosten' },
        { label: 'Nettomietrendite', value: `${nettoRendite.toFixed(2).replace('.', ',')}%`, source: `(${Math.round(jahresmiete - jahresBewirtschaftung).toLocaleString('de-DE')} / ${Math.round(gesamtkosten).toLocaleString('de-DE')})` },
      ];

      if (nettoRendite >= 5) { result.rating = 'gut'; result.interpretation = `Mit ${nettoRendite.toFixed(1).replace('.', ',')}% ist die Nettomietrendite stark — Bewirtschaftungskosten sind eingepreist.`; }
      else if (nettoRendite >= 3.5) { result.rating = 'akzeptabel'; result.interpretation = `${nettoRendite.toFixed(1).replace('.', ',')}% nach Bewirtschaftungskosten — solide, aber kein Puffer.`; }
      else { result.rating = 'kritisch'; result.interpretation = `${nettoRendite.toFixed(1).replace('.', ',')}% nach Bewirtschaftungskosten — kaum Ertrag nach realen Kosten.`; }
      break;
    }

    case 'kaufpreisfaktor': {
      const kp = baseline.kaufpreis || 0;
      const jahresmiete = (baseline.monatskaltmiete || 0) * 12;
      const faktor = jahresmiete > 0 ? kp / jahresmiete : 0;

      result.calculation = [
        { label: 'Kaufpreis', value: `${Math.round(kp).toLocaleString('de-DE')} €`, source: 'laut Inserat' },
        { label: 'Jahreskaltmiete', value: `${Math.round(jahresmiete).toLocaleString('de-DE')} €`, source: `${Math.round(baseline.monatskaltmiete || 0).toLocaleString('de-DE')} € × 12` },
        { label: 'Faktor', value: `${faktor.toFixed(1).replace('.', ',')}×`, source: `${Math.round(kp).toLocaleString('de-DE')} / ${Math.round(jahresmiete).toLocaleString('de-DE')}` },
      ];

      if (faktor > 0 && faktor < 15) { result.rating = 'gut'; result.interpretation = `Faktor ${faktor.toFixed(1).replace('.', ',')}× ist ausgezeichnet — schnelle Amortisation.`; }
      else if (faktor <= 20) { result.rating = 'gut'; result.interpretation = `Faktor ${faktor.toFixed(1).replace('.', ',')}× ist gut.`; }
      else if (faktor <= 25) { result.rating = 'akzeptabel'; result.interpretation = `Faktor ${faktor.toFixed(1).replace('.', ',')}× ist akzeptabel — im Rahmen.`; }
      else { result.rating = 'kritisch'; result.interpretation = `Faktor ${faktor.toFixed(1).replace('.', ',')}× ist hoch. Typisch für A-Städte, aber prüfenswürdig.`; }
      break;
    }

    case 'cashflow': {
      const miete = cf.einnahmen || 0;
      const bewirtschaftung = cf.bewirtschaftung || Math.round((baseline.wohnflaeche || 0) * 1.00);
      const zins = cf.davonZins || 0;
      const cashflowVal = cf.cashflow ?? (miete - zins - bewirtschaftung);

      result.calculation = [
        { label: 'Kaltmiete', value: `+${Math.round(miete).toLocaleString('de-DE')} €`, source: 'Marktmiete' },
        { label: 'Zinskosten', value: `-${Math.round(zins).toLocaleString('de-DE')} €`, source: `${((baseline.zinssatz || 0.035) * 100).toFixed(1)}% Sollzins` },
        { label: 'Bewirtschaftung', value: `-${Math.round(bewirtschaftung).toLocaleString('de-DE')} €`, source: `1 €/m² × ${baseline.wohnflaeche || 0} m²` },
        { label: 'Cashflow', value: `${cashflowVal >= 0 ? '+' : ''}${Math.round(cashflowVal).toLocaleString('de-DE')} €`, source: 'Kaltmiete - Zins - Bewirtschaftung' },
      ];

      if (cashflowVal > 200) { result.rating = 'gut'; result.interpretation = 'Die Miete deckt Zins und Bewirtschaftung mit Polster.'; }
      else if (cashflowVal >= 0) { result.rating = 'akzeptabel'; result.interpretation = 'Die Miete deckt Zins und Bewirtschaftung.'; }
      else { result.rating = 'kritisch'; result.interpretation = 'Die Miete reicht nicht für Zins und Bewirtschaftung.'; }
      break;
    }

    case 'ekRendite': {
      const ek = baseline.eigenkapital || 0;
      // cashflowJahr = Cashflow vor Tilgung (Hauptwert)
      const cfJahr = cf.cashflowJahr || 0;
      const ekRendite = baseline.ekRendite || 0;

      result.calculation = [
        { label: 'Cashflow (Jahr)', value: `${cfJahr >= 0 ? '+' : ''}${Math.round(cfJahr).toLocaleString('de-DE')} €`, source: '(Miete - Zins - Bewirtschaftung) × 12' },
        { label: 'Eigenkapital', value: `${Math.round(ek).toLocaleString('de-DE')} €`, source: `${((baseline.eigenkapitalAnteil || 0.2) * 100).toFixed(0)}% der Gesamtkosten` },
        { label: 'EK-Rendite', value: `${ekRendite.toFixed(1).replace('.', ',')}%`, source: `${Math.round(cfJahr).toLocaleString('de-DE')} / ${Math.round(ek).toLocaleString('de-DE')}` },
      ];

      if (ekRendite >= 8) { result.rating = 'gut'; result.interpretation = 'Starke Eigenkapitalrendite — guter Hebeleffekt.'; }
      else if (ekRendite >= 4) { result.rating = 'akzeptabel'; result.interpretation = 'Solide Eigenkapitalverzinsung.'; }
      else { result.rating = 'kritisch'; result.interpretation = 'Niedrige EK-Rendite — Hebel wirkt kaum positiv.'; }
      break;
    }

    default:
      break;
  }

  return result;
}
